import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CombinedContext } from "../../context/combinedContext";
import { clearTokens, setTokens } from "../../auth/tokenManager";
import useSignOutOnEntry from "../../auth/useSignOutOnEntry";
import PhoneInput from "../common/phoneInput/phoneInput";
import translations from "../../translations/translations";
import companyBasedRequest from "../../request/companyBasedRequest";
import CATEGORY_OPTIONS from "../../constants/categoryOptions";
import TermsOfServiceModal from "../common/termsOfServiceModal/termsOfServiceModal";
import PasswordField from "../common/passwordField/passwordField";
import PasswordStrength from "../common/passwordStrength/passwordStrength";
import { Check, Store, X } from "lucide-react";
import RegisterPageSkeleton from "./registerShopSkeleton/registerShopSkeleton";
import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";
import logo from "../../images/LogoFullText.png";
import "./registerShop.css";

const RegisterShopAndAdminFlow = () => {
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");
	const navigate = useNavigate();

	const [rememberMe, setRememberMe] = useState(false);
	const [verifying, setVerifying] = useState(true);
	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [phone, setPhone] = useState("");
	const [shopName, setShopName] = useState("");
	const [category, setCategory] = useState("");
	const [subCategory, setSubCategory] = useState("");
	const [openDropdown, setOpenDropdown] = useState(null);
	/* Does the owner personally take appointments? Defaults to yes, which is what
	   every shop registered before this question existed did. Answering no means
	   no calendar is created for them and their first staff member holds the
	   shop's first calendar instead. */
	const [ownerTakesBookings, setOwnerTakesBookings] = useState(true);
	const [referralCode, setReferralCode] = useState("");
	const [acceptedTerms, setAcceptedTerms] = useState(false);
	const [activeModal, setActiveModal] = useState(null);
	const [banner, setBanner] = useState(null);
	const [exiting, setExiting] = useState(false);
	const [invalidFields, setInvalidFields] = useState(new Set());

	const { userData, setUserData, language } = useContext(CombinedContext);

	/* Arriving here signs out whoever was already logged in — see the hook. */
	useSignOutOnEntry();

	const showBanner = (type, text, duration = 4000) => {
		setBanner({ type, text });
		setExiting(false);
		setTimeout(() => setExiting(true), duration - 250);
		setTimeout(() => {
			setBanner(null);
			setExiting(false);
		}, duration);
	};

	const showPersistentBanner = (type, text) => {
		setBanner({ type, text });
		setExiting(false);
	};

	useEffect(() => {
		const verifyInvite = async () => {
			try {
				const res = await companyBasedRequest.verifyShopInvite(token);
				setEmail(res.email || "");
				setFirstName(res.firstName || "");
				setLastName(res.lastName || "");
				setPhone(res.phone || "");
				setCategory(res.category || "");
				setSubCategory(res.subCategory || "");
				setReferralCode(res.referralCode || "");
				setShopName(res.shopName);
			} catch (err) {
				const code = err.response?.data?.error;

				const messages = {
					owner_exists: {
						es: "Este correo ya está vinculado a otro negocio. Usa una dirección diferente para crear este negocio.",
						en: "This email is already linked to another business. Please use a different email to create this one.",
					},
					customer_exists: {
						es: "Ya tienes una cuenta de cliente activa con este correo. Desactívala o usa un correo diferente para continuar con tu invitación.",
						en: "You already have an active customer account with this email. Deactivate it or use a different email to continue with your invite.",
					},
				};

				const msg =
					messages[code]?.[language] ??
					(language === "es" ? "Este enlace de invitación ya fue usado o expiró. Solicita uno nuevo." : "This invite link has already been used or has expired. Please request a new one.");

				showBanner("error", msg);
			} finally {
				setVerifying(false);
			}
		};

		verifyInvite();
	}, [token]);

	if (verifying) return <RegisterPageSkeleton />;

	function isStrongPassword(pwd) {
		return (
			typeof pwd === "string" &&
			pwd.length >= 8 &&
			/[A-Z]/.test(pwd) &&
			/[a-z]/.test(pwd) &&
			/[0-9]/.test(pwd) &&
			/[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(pwd)
		);
	}

	/* The banner says what's wrong; these say where. A flagged field clears
	   as soon as it's touched, so the red only ever marks what's still open. */
	const clearInvalid = (field) => {
		setInvalidFields((prev) => {
			if (!prev.has(field)) return prev;
			const next = new Set(prev);
			next.delete(field);
			return next;
		});
	};

	const invalidClass = (field) => (invalidFields.has(field) ? " is-invalid" : "");

	const handleRegisterAdmin = async () => {
		const missing = new Set();
		if (!firstName) missing.add("firstName");
		if (!lastName) missing.add("lastName");
		if (!phone) missing.add("phone");
		if (!password) missing.add("password");
		if (!confirmPassword) missing.add("confirmPassword");
		if (!category) missing.add("category");
		if (!subCategory) missing.add("subCategory");

		if (missing.size > 0) {
			setInvalidFields(missing);
			showBanner("error", language === "es" ? "¡Casi listo! Completa los campos obligatorios para continuar." : "Almost there! Please fill in the required fields to continue.");
			return;
		}

		if (!acceptedTerms) {
			setInvalidFields(new Set(["terms"]));
			showBanner("error", translations[language].registerShop.errors.mustAcceptTerms);
			return;
		}

		if (!isStrongPassword(password)) {
			setInvalidFields(new Set(["password"]));
			showBanner(
				"error",
				language === "es"
					? "Tu contraseña necesita al menos 8 caracteres, con una mayúscula, una minúscula, un número y un carácter especial."
					: "Your password needs at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character.",
			);
			return;
		}

		if (password !== confirmPassword) {
			setInvalidFields(new Set(["confirmPassword"]));
			showBanner("error", language === "es" ? "Las contraseñas no coinciden. Revísalas e inténtalo de nuevo." : "Those passwords don't match — give it another look.");
			return;
		}

		setInvalidFields(new Set());
		setLoading(true);

		try {
			const payload = {
				token,
				password,
				firstName,
				lastName,
				shopName,
				phone,
				category,
				subCategory,
				ownerTakesBookings,
				referralCode: referralCode || null,
				acceptedTermsVersion: "1.0",
			};

			const res = await companyBasedRequest.registerAdmin(payload);

			const formattedData = {
				...res,
				cognitoTokens: {
					IdToken: res.cognitoTokens?.IdToken || res.IdToken,
					AccessToken: res.cognitoTokens?.AccessToken || res.AccessToken,
					RefreshToken: res.cognitoTokens?.RefreshToken || res.RefreshToken,
				},
			};

			setUserData(formattedData);

			const storage = rememberMe ? localStorage : sessionStorage;
			// Centralised so a leftover token in the other storage cannot
			// shadow this brand-new session.
			clearTokens();
			setTokens(
				{
					idToken: formattedData.cognitoTokens?.IdToken,
					accessToken: formattedData.cognitoTokens?.AccessToken,
					refreshToken: formattedData.cognitoTokens?.RefreshToken,
				},
				{ storage }
			);
			storage.setItem("user", JSON.stringify(formattedData.user));

			navigate("/calendar");
		} catch (err) {
			console.error("Register admin failed:", err);

			const code = err.response?.data?.error;

			const errorMessages = {
				email_in_use: {
					es: "Ya existe una cuenta con este correo electrónico. Inicia sesión o usa un correo diferente.",
					en: "An account with this email already exists. Please sign in or use a different email.",
				},
				invalid_password: {
					es: "Tu contraseña no cumple los requisitos mínimos (mayúscula, minúscula y un número, mínimo 8 caracteres).",
					en: "Your password doesn't meet the minimum requirements (uppercase, lowercase, and a number, at least 8 characters).",
				},
				invalid_parameter: {
					es: "Revisa tu correo y número de teléfono — alguno de los dos no tiene un formato válido.",
					en: "Please check your email and phone number — one of them isn't in a valid format.",
				},
				too_many_requests: {
					es: "Demasiados intentos. Espera un momento e inténtalo de nuevo.",
					en: "Too many attempts. Please wait a moment and try again.",
				},
			};

			const msg =
				errorMessages[code]?.[language] ??
				err.message ??
				(language === "es" ? "No pudimos registrar tu cuenta de administrador. Inténtalo de nuevo." : "We couldn't register your admin account. Please try again.");

			showPersistentBanner("error", msg);
		} finally {
			setLoading(false);
		}
	};

	const selectedCategoryObj = CATEGORY_OPTIONS.find((c) => c.value === category);
	const availableSubCategories = selectedCategoryObj?.subCategories || [];

	const t = translations[language].registerShop.step1;

	return (
		<div className="sf-auth-page is-wide sf-rs-root">
			<div className="sf-auth-shell">
				<div className="sf-auth-card">
					<div className="sf-auth-head">
						<h1 className="sf-auth-title">{t.title}</h1>
						<p className="sf-auth-sub">{t.subtitle}</p>
					</div>

					{/* What this invite is for — the one thing they can't change here */}
					{shopName && (
						<div className="sf-auth-callout">
							<span className="sf-auth-callout-icon">
								<Store size={18} strokeWidth={2} />
							</span>
							<span className="sf-auth-callout-copy">
								<em>{t.invitedTo}</em>
								<strong>{shopName}</strong>
							</span>
						</div>
					)}

					<p className="sf-auth-legend">{t.sectionAbout}</p>

					{/* Email — prefilled, read only */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.email || "Email"}</label>
						<input className="sf-auth-input" type="email" value={email} disabled />
					</div>

					{/* Name row */}
					<div className="sf-auth-row2">
						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.firstName}</label>
							<input
								className={`sf-auth-input${invalidClass("firstName")}`}
								placeholder={t.firstName}
								value={firstName}
								onChange={(e) => {
									setFirstName(e.target.value);
									clearInvalid("firstName");
								}}
								autoComplete="given-name"
							/>
						</div>
						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.lastName}</label>
							<input
								className={`sf-auth-input${invalidClass("lastName")}`}
								placeholder={t.lastName}
								value={lastName}
								onChange={(e) => {
									setLastName(e.target.value);
									clearInvalid("lastName");
								}}
								autoComplete="family-name"
							/>
						</div>
					</div>

					{/* Phone */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.phone}</label>
						<PhoneInput
							businessPhone={phone}
							setBusinessPhone={(value) => {
								setPhone(value);
								clearInvalid("phone");
							}}
							className={invalidFields.has("phone") ? "is-invalid" : ""}
						/>
					</div>

					<p className="sf-auth-legend">{t.sectionSecurity}</p>

					{/* Password */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.password}</label>
						<PasswordField
							value={password}
							onChange={(value) => {
								setPassword(value);
								clearInvalid("password");
							}}
							placeholder={t.password}
							autoComplete="new-password"
							className={invalidClass("password").trim()}
						/>
						<PasswordStrength value={password} />
					</div>

					{/* Confirm password */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.confirmPassword}</label>
						<PasswordField
							value={confirmPassword}
							onChange={(value) => {
								setConfirmPassword(value);
								clearInvalid("confirmPassword");
							}}
							placeholder={t.confirmPassword}
							autoComplete="new-password"
							className={invalidClass("confirmPassword").trim()}
						/>
						{confirmPassword && (
							<p className={`sf-auth-match ${password === confirmPassword ? "is-ok" : "is-bad"}`} aria-live="polite">
								{password === confirmPassword ? <Check size={13} strokeWidth={3} /> : <X size={13} strokeWidth={3} />}
								{password === confirmPassword
									? translations[language].register.match.ok
									: translations[language].register.match.bad}
							</p>
						)}
					</div>

					<p className="sf-auth-legend">{t.sectionBusiness}</p>

					{/* Category row */}
					<div className="sf-auth-row2">
						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.category}</label>
							<div className="sf-auth-select">
								<button
									type="button"
									className={`sf-auth-select-btn${category ? " is-selected" : ""}${openDropdown === "category" ? " is-open" : ""}${invalidClass("category")}`}
									onClick={() => setOpenDropdown(openDropdown === "category" ? null : "category")}
								>
									<span>
										{category
											? language === "es"
												? selectedCategoryObj.labelES
												: selectedCategoryObj.labelEN
											: t.categoryPlaceholder}
									</span>
									<span className={`sf-auth-caret${openDropdown === "category" ? " is-open" : ""}`}>▼</span>
								</button>
								{openDropdown === "category" && (
									<ul className="sf-auth-select-list">
										{CATEGORY_OPTIONS.map((cat) => (
											<li
												key={cat.value}
												className={`sf-auth-select-item${category === cat.value ? " is-active" : ""}`}
												onClick={() => {
													setCategory(cat.value);
													setSubCategory("");
													setOpenDropdown(null);
													clearInvalid("category");
												}}
											>
												{language === "es" ? cat.labelES : cat.labelEN}
											</li>
										))}
									</ul>
								)}
							</div>
						</div>

						<div className="sf-auth-field">
							<label className="sf-auth-label">{t.subCategory}</label>
							<div className="sf-auth-select">
								<button
									type="button"
									className={`sf-auth-select-btn${subCategory ? " is-selected" : ""}${openDropdown === "subcategory" ? " is-open" : ""}${invalidClass("subCategory")}`}
									disabled={!category}
									onClick={() => setOpenDropdown(openDropdown === "subcategory" ? null : "subcategory")}
								>
									<span>
										{subCategory
											? language === "es"
												? availableSubCategories.find((s) => s.value === subCategory)?.labelES
												: availableSubCategories.find((s) => s.value === subCategory)?.labelEN
											: t.subCategoryPlaceholder}
									</span>
									<span className={`sf-auth-caret${openDropdown === "subcategory" ? " is-open" : ""}`}>▼</span>
								</button>
								{openDropdown === "subcategory" && (
									<ul className="sf-auth-select-list">
										{availableSubCategories.map((sub) => (
											<li
												key={sub.value}
												className={`sf-auth-select-item${subCategory === sub.value ? " is-active" : ""}`}
												onClick={() => {
													setSubCategory(sub.value);
													setOpenDropdown(null);
													clearInvalid("subCategory");
												}}
											>
												{language === "es" ? sub.labelES : sub.labelEN}
											</li>
										))}
									</ul>
								)}
							</div>
						</div>
					</div>

					{/* Who takes the appointments. Asked here because it decides whether a
					    calendar is created for this account at all — changeable later from
					    the Team page, but far cheaper to get right up front. */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.ownerTakesBookings.label}</label>
						<div className="sf-rs-choice-group" role="radiogroup" aria-label={t.ownerTakesBookings.label}>
							<button
								type="button"
								role="radio"
								aria-checked={ownerTakesBookings}
								className={`sf-rs-choice${ownerTakesBookings ? " is-picked" : ""}`}
								onClick={() => setOwnerTakesBookings(true)}
							>
								<span className="sf-rs-choice-title">{t.ownerTakesBookings.yes}</span>
								<span className="sf-rs-choice-hint">{t.ownerTakesBookings.yesHint}</span>
							</button>
							<button
								type="button"
								role="radio"
								aria-checked={!ownerTakesBookings}
								className={`sf-rs-choice${!ownerTakesBookings ? " is-picked" : ""}`}
								onClick={() => setOwnerTakesBookings(false)}
							>
								<span className="sf-rs-choice-title">{t.ownerTakesBookings.no}</span>
								<span className="sf-rs-choice-hint">{t.ownerTakesBookings.noHint}</span>
							</button>
						</div>
						<p className="sf-rs-choice-note">{ownerTakesBookings ? t.ownerTakesBookings.noteYes : t.ownerTakesBookings.noteNo}</p>
					</div>

					{/* Referral code */}
					<div className="sf-auth-field">
						<label className="sf-auth-label">{t.referralCode}</label>
						<input
							className="sf-auth-input"
							placeholder={t.referralCode}
							value={referralCode}
							onChange={(e) => setReferralCode(e.target.value)}
						/>
					</div>

					{/* Remember me */}
					<div className="sf-auth-check is-quiet">
						<input type="checkbox" id="rememberMe" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
						<label htmlFor="rememberMe">{t.remember}</label>
					</div>

					{/* Terms */}
					<div className={`sf-auth-check${invalidClass("terms")}`}>
						<input
							type="checkbox"
							id="acceptTerms"
							checked={acceptedTerms}
							onChange={(e) => {
								setAcceptedTerms(e.target.checked);
								clearInvalid("terms");
							}}
						/>
						<label htmlFor="acceptTerms">
							{translations[language].register.acceptTerms}{" "}
							<span className="sf-auth-check-link" onClick={() => setActiveModal("terms")}>
								{translations[language].register.termsLink}
							</span>
						</label>
					</div>

					{/* Submit */}
					<button className="sf-auth-cta" onClick={handleRegisterAdmin} disabled={loading}>
						{loading && <span className="sf-auth-spinner" aria-hidden="true" />}
						{loading ? t.registering : t.register}
					</button>
				</div>
			</div>

			{banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}
			{activeModal === "terms" && <TermsOfServiceModal isOpen onClose={() => setActiveModal(null)} />}
		</div>
	);
};

export default RegisterShopAndAdminFlow;

// import React, { useState, useEffect, useContext, useRef } from "react";
// import { useNavigate, useSearchParams } from "react-router-dom";
// import usersRequest from "../../request/usersRequest";
// import { CombinedContext } from "../../context/combinedContext";
// import PhoneInput from "../common/phoneInput/phoneInput";
// import imageCompression from "browser-image-compression";
// import translations from "../../translations/translations";
// import "./registerShop.css";
// import companyBasedRequest from "../../request/companyBasedRequest";
// import { Camera, UserCircle2 } from "lucide-react";
// import CATEGORY_OPTIONS from "../../constants/categoryOptions";
// import TermsOfServiceModal from "../common/termsOfServiceModal/termsOfServiceModal";
// import PasswordField from "../common/passwordField/passwordField";
// import RegisterPageSkeleton from "./registerShopSkeleton/registerShopSkeleton";
// import BannerMessageFixed from "../common/bannerMessageFixed/bannerMessageFixed";

// const RegisterShopAndAdminFlow = () => {
//     const [searchParams] = useSearchParams();
//     const token = searchParams.get("token");
//     const navigate = useNavigate();

//     const [rememberMe, setRememberMe] = useState(false);
//     const [inviteValid, setInviteValid] = useState(false);
//     const [verifying, setVerifying] = useState(true);
//     const [adminId, setAdminId] = useState("");
//     const [success, setSuccess] = useState("");
//     const [loading, setLoading] = useState(false);
//     const [email, setEmail] = useState("");
//     const [password, setPassword] = useState("");
//     const [confirmPassword, setConfirmPassword] = useState("");
//     const [firstName, setFirstName] = useState("");
//     const [lastName, setLastName] = useState("");
//     const [phone, setPhone] = useState("");
//     const [adminImage, setAdminImage] = useState(null);
//     const [category, setCategory] = useState(""); // Beauty, Self-Care, etc.
//     const [subCategory, setSubCategory] = useState("");
//     const dropdownRef = useRef(null);
//     const [openDropdown, setOpenDropdown] = useState(false);
//     const [referralCode, setReferralCode] = useState("");
//     const [activeModal, setActiveModal] = useState(null);
//     const [acceptedTerms, setAcceptedTerms] = useState(false);
//     const [banner, setBanner] = useState(null);
//     const [exiting, setExiting] = useState(false);

//     const fileInputRef = useRef(null);
//     const maxSlots = 5;

//     const { userData, setUserData, language } = useContext(CombinedContext);

//     const showBanner = (type, text, duration = 2500) => {
//         setBanner({ type, text });
//         setExiting(false);

//         setTimeout(() => {
//             setExiting(true);
//         }, duration - 250);

//         setTimeout(() => {
//             setBanner(null);
//             setExiting(false);
//         }, duration);
//     };

//     useEffect(() => {
//         // if (userData?.user?.role === "admin") return;

//         const verifyInvite = async () => {
//             try {
//                 const res = await companyBasedRequest.verifyShopInvite(token);

//                 setEmail(res.email || "");
//                 setFirstName(res.firstName || "");
//                 setLastName(res.lastName || "");
//                 setPhone(res.phone || "");
//                 setCategory(res.category || "");
//                 setSubCategory(res.subCategory || "");

//                 setReferralCode(res.referralCode || "");

//                 setInviteValid(true);

//                 if (res.adminVerified && !res.used) {
//                     if (!userData || !userData.cognitoTokens?.IdToken) {
//                         navigate("/signin");
//                         return;
//                     }
//                 }

//                 // ✅ Mark as verified
//                 sessionStorage.setItem("inviteVerified", "true");
//             } catch (err) {
//                 showBanner(
//                     "error",
//                     language === "es" ? "Enlace de invitación inválido o expirado" : "Invalid or expired invite link"
//                 );
//             } finally {
//                 setVerifying(false);
//             }
//         };

//         verifyInvite();
//     }, [token]);

//     useEffect(() => {
//         if (userData && userData.user.role === "owner" && userData.user.onboardingStatus === "pending-shop") {
//             setAdminId(userData.userId);
//         }
//     }, [userData]);

//     if (verifying) {
//         return <RegisterPageSkeleton />;
//     }

//     function isStrongPassword(password) {
//         return (
//             typeof password === "string" &&
//             password.length >= 8 &&
//             /[A-Z]/.test(password) &&
//             /[a-z]/.test(password) &&
//             /[0-9]/.test(password) &&
//             /[\^$*.\[\]{}()?"!@#%&/\\,><':;|_~`+=-]/.test(password)
//         );
//     }

//     const handleRegisterAdmin = async () => {
//         if (!firstName || !lastName || !phone || !password || !confirmPassword || !category || !subCategory) {
//             showBanner(
//                 "error",
//                 language === "es"
//                     ? "Por favor completa todos los campos obligatorios"
//                     : "Please fill out all required fields"
//             );
//             return;
//         }

//         if (!acceptedTerms) {
//             showBanner("error", translations[language].registerShop.errors.mustAcceptTerms);
//             return;
//         }

//         if (!isStrongPassword(password)) {
//             showBanner(
//                 "error",
//                 language === "es"
//                     ? "La contraseña debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un carácter especial"
//                     : "Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a special character"
//             );
//             return;
//         }

//         if (password !== confirmPassword) {
//             showBanner("error", language === "es" ? "Las contraseñas no coinciden" : "Passwords do not match");
//             return;
//         }

//         setLoading(true);

//         try {
//             const payload = {
//                 token,
//                 password,
//                 firstName,
//                 lastName,
//                 phone: phone,
//                 category,
//                 subCategory,
//                 referralCode: referralCode || null,
//                 acceptedTermsVersion: "1.0",
//             };

//             const res = await companyBasedRequest.registerAdmin(payload);

//             // 🧠 Normalize structure to match login and context
//             const formattedData = {
//                 ...res,
//                 cognitoTokens: {
//                     IdToken: res.cognitoTokens?.IdToken || res.IdToken,
//                     AccessToken: res.cognitoTokens?.AccessToken || res.AccessToken,
//                     RefreshToken: res.cognitoTokens?.RefreshToken || res.RefreshToken,
//                 },
//             };

//             setUserData(formattedData);
//             setAdminId(res.adminId);
//             navigate("/calendar");

//             // 💾 Store tokens & user persistently
//             const storage = rememberMe ? localStorage : sessionStorage;
//             if (formattedData.cognitoTokens?.IdToken) storage.setItem("idToken", formattedData.cognitoTokens.IdToken);
//             if (formattedData.cognitoTokens?.AccessToken)
//                 storage.setItem("accessToken", formattedData.cognitoTokens.AccessToken);
//             if (formattedData.cognitoTokens?.RefreshToken)
//                 storage.setItem("refreshToken", formattedData.cognitoTokens.RefreshToken);
//             storage.setItem("user", JSON.stringify(formattedData.user));
//         } catch (err) {
//             console.error("Register admin failed:", err);
//             showBanner(
//                 "error",
//                 err.message || (language === "es" ? "Error al registrar administrador" : "Failed to register admin")
//             );
//         } finally {
//             setLoading(false);
//         }
//     };

//     const getLabel = (item, language) => {
//         if (!item) return "";
//         return language === "es" ? item.labelES : item.labelEN;
//     };

//     const selectedCategoryObj = CATEGORY_OPTIONS.find((c) => c.value === category);
//     const availableSubCategories = selectedCategoryObj?.subCategories || [];

//     return (
//         <div className="container">
//             <div className="card" style={{ maxWidth: 400, margin: "40px auto" }}>
//                 <div className="bd">
//                     {success && (
//                         <div className="sub" style={{ color: "green", marginBottom: 10 }}>
//                             {success}
//                         </div>
//                     )}

//                     <>
//                         <div className="row" style={{ marginBottom: 10 }}>
//                             <div className="sf-registershop-title">
//                                 {translations[language].registerShop.step1.title}
//                             </div>
//                             <input className="input" type="email" value={email} disabled />
//                             <input
//                                 className="input"
//                                 placeholder={translations[language].registerShop.step1.firstName}
//                                 value={firstName}
//                                 onChange={(e) => setFirstName(e.target.value)}
//                                 style={{ marginRight: 5 }}
//                             />
//                             <input
//                                 className="input"
//                                 placeholder={translations[language].registerShop.step1.lastName}
//                                 value={lastName}
//                                 onChange={(e) => setLastName(e.target.value)}
//                             />

//                             <PhoneInput businessPhone={phone} setBusinessPhone={setPhone} />

//                             <PasswordField
//                                 value={password}
//                                 onChange={setPassword}
//                                 placeholder={translations[language].registerShop.step1.password}
//                                 autoComplete="new-password"
//                             />

//                             <PasswordField
//                                 value={confirmPassword}
//                                 onChange={setConfirmPassword}
//                                 placeholder={translations[language].registerShop.step1.confirmPassword}
//                                 autoComplete="new-password"
//                             />

//                             <input
//                                 className="input"
//                                 placeholder={translations[language].registerShop.step1.referralCode}
//                                 value={referralCode}
//                                 onChange={(e) => setReferralCode(e.target.value)}
//                             />

//                             {/* <div className="sub" style={{ marginBottom: 10, fontWeight: "500" }}>
//                                 {translations[language].registerShop.step1.category || "Business category"}
//                             </div> */}

//                             <div className="category-input-container" ref={dropdownRef}>
//                                 <button
//                                     type="button"
//                                     className="category-dropdown-btn"
//                                     onClick={() => setOpenDropdown(openDropdown === "category" ? null : "category")}
//                                 >
//                                     {category
//                                         ? language === "es"
//                                             ? selectedCategoryObj.labelES
//                                             : selectedCategoryObj.labelEN
//                                         : language === "es"
//                                         ? "Tipo de negocio"
//                                         : "Business category"}
//                                     <span className={`arrow ${openDropdown === "category" ? "open" : ""}`}>▼</span>
//                                 </button>

//                                 {openDropdown === "category" && (
//                                     <ul className="category-dropdown-list">
//                                         {CATEGORY_OPTIONS.map((cat) => (
//                                             <li
//                                                 key={cat.value}
//                                                 className="category-dropdown-item"
//                                                 onClick={() => {
//                                                     setCategory(cat.value);
//                                                     setSubCategory("");
//                                                     setOpenDropdown(null);
//                                                 }}
//                                             >
//                                                 {language === "es" ? cat.labelES : cat.labelEN}
//                                             </li>
//                                         ))}
//                                     </ul>
//                                 )}
//                             </div>

//                             <div className="category-input-container">
//                                 <button
//                                     type="button"
//                                     className="category-dropdown-btn"
//                                     onClick={() =>
//                                         setOpenDropdown(openDropdown === "subcategory" ? null : "subcategory")
//                                     }
//                                 >
//                                     {subCategory
//                                         ? language === "es"
//                                             ? availableSubCategories.find((s) => s.value === subCategory)?.labelES
//                                             : availableSubCategories.find((s) => s.value === subCategory)?.labelEN
//                                         : language === "es"
//                                         ? "Subcategoría"
//                                         : "Subcategory"}
//                                     <span className={`arrow ${openDropdown === "subcategory" ? "open" : ""}`}>▼</span>
//                                 </button>

//                                 {openDropdown === "subcategory" && (
//                                     <ul className="category-dropdown-list">
//                                         {availableSubCategories.map((sub) => (
//                                             <li
//                                                 key={sub.value}
//                                                 className="category-dropdown-item"
//                                                 onClick={() => {
//                                                     setSubCategory(sub.value);
//                                                     setOpenDropdown(null);
//                                                 }}
//                                             >
//                                                 {language === "es" ? sub.labelES : sub.labelEN}
//                                             </li>
//                                         ))}
//                                     </ul>
//                                 )}
//                             </div>
//                         </div>

//                         {/* )} */}

//                         <div
//                             className="row sf-register-terms-remember-me"
//                             style={{
//                                 marginBottom: 10,
//                             }}
//                         >
//                             <div
//                                 // className="sf-register-terms-remember-me"
//                                 style={{
//                                     // marginBottom: 10,
//                                     display: "flex",
//                                     alignItems: "center",
//                                 }}
//                             >
//                                 <input
//                                     type="checkbox"
//                                     id="rememberMe"
//                                     checked={rememberMe}
//                                     onChange={(e) => setRememberMe(e.target.checked)}
//                                     style={{ marginRight: 6 }}
//                                 />
//                                 <label htmlFor="rememberMe">{translations[language].registerShop.step1.remember}</label>
//                             </div>

//                             <div className="terms-checkbox">
//                                 <label className="terms-label">
//                                     <input
//                                         type="checkbox"
//                                         checked={acceptedTerms}
//                                         onChange={(e) => setAcceptedTerms(e.target.checked)}
//                                         style={{ marginRight: 6 }}
//                                     />
//                                     <span>
//                                         {translations[language].register.acceptTerms}{" "}
//                                         <span className="sf-terms-span" onClick={() => setActiveModal("terms")}>
//                                             {translations[language].register.termsLink}
//                                         </span>
//                                     </span>
//                                 </label>
//                             </div>
//                         </div>

//                         {banner && <BannerMessageFixed type={banner.type} text={banner.text} exiting={exiting} />}

//                         <button
//                             className="btn brand"
//                             style={{ width: "100%", marginBottom: 10 }}
//                             onClick={handleRegisterAdmin}
//                             disabled={loading}
//                         >
//                             {loading
//                                 ? translations[language].registerShop.step1.registering
//                                 : translations[language].registerShop.step1.register}
//                         </button>
//                     </>
//                 </div>
//             </div>
//             {activeModal === "terms" && <TermsOfServiceModal isOpen onClose={() => setActiveModal(null)} />}
//         </div>
//     );
// };

// export default RegisterShopAndAdminFlow;
